import axios from 'axios';
import { axiosRequest } from '@general/js/utils/RequestAPIUtils';
import { DHLError } from '@general/js/utils';
import { Services } from '@/services/Services';
import AbstractClass from './LimitsUsageAbstractServices';

export default class LimitsUsageServices extends AbstractClass {
  /**
   * Gets reference data for limits and usage page
   * @returns {object} Object containing reference data information text
   */
  static async getReferenceData() {
    try {
      const response = await axios.get(Services.MY_CLAIMS.LIMITS_USAGE.GET_REFERENCE_DATA);

      // Response data should always returned
      if (response?.data) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Gets the limits and usage record data (extras) from OHI
   * @param {string} memberNumber Policy number
   * @param {string} asAtDate Date used to determine the limits that have been consumed up till that point in time. Format in 'YYYY-DD-YY'.
   * @returns {Array} Limits and usage array response from EAI. Response will be data that will be fed to the primary and secondary tables in extras services.
   */
  static async retrieveLimitsAndUsage(memberNumber, asAtDate) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.LIMITS_USAGE.GET_LIMITS_AND_MODALITIES(memberNumber, asAtDate));
      if (response.data) {
        if (response.data.limitsAndConsumption && Array.isArray(response.data.limitsAndConsumption)) {
          return response.data.limitsAndConsumption;
        }
        throw new Error('Issue with response object not containing limitsAndConsumption');
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`retrieveLimitsAndUsage() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Service call to fetch the replacement period data that will be shown on screen.
   * @returns {Array} The replacement periods that will be displayed for the limits.
   */
  static async retrieveReplacementPeriods() {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.LIMITS_USAGE.GET_REPLACEMENT_PERIODS);

      if (response.data) {
        if (response.data.replacementPeriods && Array.isArray(response.data.replacementPeriods)) {
          return response.data.replacementPeriods;
        }
        throw new Error('Issue with response object not containing replacementPeriods');
      }

      throw new Error(`Issues with response: ${response}`);
    } catch (error) {
      throw new DHLError(`retrieveReplacementPeriods() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Gets member history from OHI
   * @param {string} memberNumber Policy number
   * @returns {Array} List of members and their history
   */
  static async getMemberHistory(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.LIMITS_USAGE.GET_MEMBER_HISTORY(memberNumber));

      if (response.data) {
        if (response.data.members && Array.isArray(response.data.members)) {
          return response.data.members;
        }
        throw new Error('Issue with response object not containing members');
      }

      throw new Error(`Issues with response: ${response}`);
    } catch (error) {
      throw new DHLError(`getMemberHistory() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Gets the services and limits record data (hospital) from OHI
   * @param {string} memberNumber Policy number
   * @param {string} asAtDate Date used to determine the limits that have been consumed up till that point in time. Format in 'YYYY-DD-YY'.
   * @param {string} currentDate The current/today's date
   * @returns {Array} Limits and usage array response from EAI. Response will be data that will be fed to the hospital services table.
   */
  static async retrieveServicesAndLimits(memberNumber, asAtDate, currentDate) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.LIMITS_USAGE.GET_SERVICE_LIMITS(memberNumber, asAtDate, currentDate));
      if (response.data) {
        if (response.data.limitsAndConsumption && Array.isArray(response.data.limitsAndConsumption)) {
          return response.data.limitsAndConsumption;
        }
        throw new Error('Issue with response object not containing limitsAndConsumption');
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`retrieveServicesAndLimits() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
